<template>
  <div>
    <b-link class="brand-logo">
      <div class="d-flex mt-2 mb-1 mx-1">
        <add-co-logo />
        <h2 class="brand-text text-primary font-weight-bolder mb-0 ml-1">
          AddCo
        </h2>
      </div>
    </b-link>

    <!-- Loading -->
    <loading
      v-if="isLoading || isSubmitting"
      :active="true" 
      :is-full-page="true"
      color="#498ceb"
    />

    <div v-if="!isLoading" class="misc-inner m-2">
      <b-card v-if="clientNotFound">
        <h2 class="text-danger">
          {{ $t('client_not_found_check_url') }}
        </h2>
      </b-card>
    
      <div v-else>
        <div>
          <b-card>
            <h5 class="mb-2">
              {{ isSubmitted ? $t('message.complaint_sent') : $t('message.complaint_confidential') }}
            </h5>
            <h5 :class="`${isSubmitted ? 'mb-2 text-success' : 'mb-0'}`">
              {{ isSubmitted ? `${$t('message.user_number')}: ${authorUserNumber}` : $t('message.no_personal_data_recorded') }}
            </h5>
            <h5 v-if="isSubmitted" class="mb-0 text-warning">
              {{ `${$t('message.case_number')}: ${caseNumber}` }}
            </h5>
          </b-card>

          <form-wizard
            v-if="!isSubmitted"
            color="#498ceb"
            :title="null"
            :subtitle="null"
            layout="horizontal"
            :finish-button-text="$t('message.submit')"
            :back-button-text="$t('message.previous')"
            :next-button-text="$t('message.next')"
            class="wizard-vertical mb-3"
            ref="myForm"
          >
            <!-- Buttons personalization -->
            <template v-slot:footer="slotProps">
              <div class="wizard-footer d-flex justify-content-between align-items-center">
                <b-button
                  variant="outline-secondary"
                  @click="goToPreviousTab"
                  :disabled="slotProps.activeTabIndex === 0"
                >
                  < &nbsp; {{ $t('message.previous') }}
                </b-button>

                <b-button
                  v-if="!slotProps.isLastStep"
                  variant="primary"
                  @click="goToNextTab"
                >
                  {{ $t('message.next') }} &nbsp; >
                </b-button>

                <b-button
                  v-else
                  variant="success"
                  @click="handleSubmit"
                >
                  {{ $t('message.submit') }}
                </b-button>
              </div>
            </template>

            <!-- Type -->
            <tab-content :title="$t('label.complaintType')">
              <validation-observer ref="typeTab" tag="form">
                <b-row>
                  <b-col md="12">
                    <b-form-group
                      :label="$t('label.complaintType')"
                      :label-for="$t('label.complaintType')"
                    >
                      <validation-provider :name="$t('label.complaintType')" rules="required" v-slot="{ errors }">
                        <b-form-checkbox v-model="type" value="economicCrimesLaw" unchecked-value="" class="mt-1">
                          {{ $t('label.economicCrimesLaw') }}
                        </b-form-checkbox>

                        <b-form-checkbox v-model="type" value="karinLaw" unchecked-value="" class="my-1">
                          {{ $t('label.karinLaw') }}
                        </b-form-checkbox>

                        <b-form-checkbox v-model="type" value="ethicalSituations" unchecked-value="" class="my-1">
                          {{ $t('label.ethicalSituations') }}
                        </b-form-checkbox>

                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </validation-observer>
            </tab-content>

            <!-- Terms and Conditions -->
            <tab-content :title="$t('label.terms_and_conditions')">
              <validation-observer ref="termsTab" tag="form">
                <b-row>
                  <b-col md="12">
                    <b-form-group
                      :label="$t('label.terms_and_conditions')"
                      :label-for="$t('label.terms_and_conditions')"
                    >
                      <div class="border mb-1">
                        <vue-perfect-scrollbar
                          :settings="{ maxScrollbarLength: 150 }"
                          class="scroll-area"
                        >
                          <div v-if="type === 'economicCrimesLaw' || type === 'ethicalSituations'" class="py-1 pr-1">
                            <ol>
                              <li>{{ $t('message.ecl_complaint_terms_1_1') }} <u>{{ $t('message.ecl_complaint_terms_1_2') }}</u></li>
                              <li>{{ $t('message.ecl_complaint_terms_2_1') }} <u>{{ $t('message.ecl_complaint_terms_2_2') }}</u></li>
                              <li>{{ $t('message.ecl_complaint_terms_3') }}</li>
                              <li>{{ $t('message.ecl_complaint_terms_4') }}</li>
                            </ol>

                            <p class="pl-2">{{ $t('message.ecl_complaint_terms_5_1') }} <b><u>{{ $t('message.ecl_complaint_terms_5_2') }}</u></b></p>
                          </div>

                          <div v-else class="py-1 pr-1">
                            <ol>
                              <li>{{ $t('message.kl_complaint_terms_1') }}</li>
                              <li>{{ $t('message.ecl_complaint_terms_2_1') }} <u>{{ $t('message.ecl_complaint_terms_2_2') }}</u></li>
                              <li>{{ $t('message.kl_complaint_terms_2') }}</li>
                              <li>{{ $t('message.kl_complaint_terms_3') }}</li>
                              <li>{{ $t('message.ecl_complaint_terms_4') }}</li>
                            </ol>
  
                            <p class="pl-2">{{ $t('message.ecl_complaint_terms_5_1') }} <b><u>{{ $t('message.ecl_complaint_terms_5_2') }}</u></b></p>
                          </div>
                        </vue-perfect-scrollbar>
                      </div>

                      <validation-provider :name="$t('message.must_accept_terms')" rules="is:yes" v-slot="{ errors }">
                        <b-form-checkbox
                          id="checkbox-1"
                          v-model="acceptTerms"
                          name="checkbox-1"
                          value="yes"
                          unchecked-value="no"
                        >
                          {{ $t('message.accept_terms') }}
                        </b-form-checkbox>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </validation-observer>
            </tab-content>

            <!-- Complaint -->
            <tab-content :title="$t('label.complaint')">
              <validation-observer ref="complaintTab" tag="form">
                <!-- Economic Crimes Law / Ethical Situations -->
                <div v-if="type === 'economicCrimesLaw' || type === 'ethicalSituations'">
                  <b-row>
                    <!-- Description -->
                    <b-col md="12">
                      <b-form-group :label="$t(`label.${type === 'economicCrimesLaw' ? 'complaint' : 'ethicalSituation'}`)" label-for="complaint">
                        <validation-provider :name="$t(`label.${type === 'economicCrimesLaw' ? 'complaint' : 'ethicalSituation'}`)" rules="required" v-slot="{ errors }">
                          <b-form-textarea
                            v-model="complaint.description"
                            rows="2"
                            :placeholder="$t(`message.${type === 'economicCrimesLaw' ? 'describe_what_when_where' : 'describe_ethical_situation'}`)"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                  </b-row>
  
                  <b-row>
                    <!-- Reported Person -->
                    <b-col md="6">
                      <b-form-group :label="$t(`label.${type === 'economicCrimesLaw' ? 'reported_person' : 'involved_person'}`)" label-for="reported_person">
                        <validation-provider :name="$t(`label.${type === 'economicCrimesLaw' ? 'reported_person' : 'involved_person'}`)" rules="required" v-slot="{ errors }">
                          <v-select
                            v-model="complaint.reportedPerson"
                            label="title"
                            taggable
                            push-tags
                            :multiple="true"
                            :options="employees"
                            :create-option="name => ({ title: name, value: name, addNewOption: true })"
                            :placeholder="$t(`placeholder.${type === 'economicCrimesLaw' ? 'reported_person' : 'involved_person'}`)"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
  
                    <!-- Reported Person Responsible -->
                    <b-col md="6">
                      <b-form-group :label="$t(`label.${type === 'economicCrimesLaw' ? 'reported_person_responsible' : 'involved_person_responsible'}`)" label-for="reported_person_responsible">
                        <v-select
                          v-model="complaint.reportedPersonResponsible"
                          label="title"
                          taggable
                          push-tags
                          :options="employees"
                          :create-option="name => ({ title: name, value: name, addNewOption: true })"
                          :placeholder="$t(`placeholder.${type === 'economicCrimesLaw' ? 'reported_person_responsible' : 'involved_person_responsible'}`)"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
  
                  <b-row>
                    <!-- Operational Areas Involved -->
                    <b-col md="6">
                      <b-form-group :label="$t('label.operationalAreasInvolved')" label-for="operationalAreasInvolved">
                        <v-select
                          v-model="complaint.areas"
                          label="title"
                          taggable
                          push-tags
                          :multiple="true"
                          :options="areas"
                          :create-option="name => ({ title: name, value: name, addNewOption: true })"
                          :placeholder="$t('placeholder.operationalAreasInvolved')"
                        />
                      </b-form-group>
                    </b-col>
  
                    <!-- Document -->
                    <b-col md="6">
                      <b-form-group :label="$t('label.document')" label-for="document">
                        <!-- Document Repository -->
                        <document-repository
                          :documents="documentFiles"
                          :keys="[]"
                          :read-only="false"
                          @add="handleAddDocuments"
                          @delete="handleDeleteDocuments"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
  
                  <b-row>
                    <!-- Themes -->
                    <b-col md="6">
                      <b-form-group :label="$t('label.themes')" label-for="themes">
                        <validation-provider :name="$t('label.themes')" rules="required" v-slot="{ errors }">
                          <v-select
                            v-model="complaint.themes"
                            label="title"
                            taggable
                            push-tags
                            :multiple="true"
                            :options="complaintThemes"
                            :create-option="name => ({ title: name, value: 'newOption' })"
                            :placeholder="$t('placeholder.themes')"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>

                    <!-- Company Relationship -->
                    <b-col md="6">
                      <b-form-group :label="$t('label.companyRelationship')" label-for="companyRelationship">
                        <v-select
                          v-model="complaint.companyRelationship"
                          label="title"
                          taggable
                          push-tags
                          :options="complaintRelationshipOptions"
                          :create-option="name => ({ title: name, value: 'newOption' })"
                          :placeholder="$t('placeholder.companyRelationship')"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <!-- Company -->
                    <b-col md="6">
                      <b-form-group :label="$t('label.company')" label-for="company">
                        <validation-provider :name="$t('label.company')" rules="required" v-slot="{ errors }">
                          <v-select
                            v-model="complaint.company"
                            label="title"
                            :options="companies"
                            :placeholder="$t('placeholder.company')"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>

                <!-- Karin Law -->
                <div v-else>
                  <b-row>
                    <!-- Themes -->
                    <b-col md="6">
                      <b-form-group :label="$t('label.themes')" label-for="themes">
                        <validation-provider :name="$t('label.themes')" rules="required" v-slot="{ errors }">
                          <v-select
                            v-model="complaint.themes"
                            label="title"
                            :multiple="true"
                            :options="karinLawThemes"
                            :placeholder="$t('placeholder.themes')"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <!-- Author -->
                    <b-col md="4" class="border-right">
                      <b-form-group :label="$t('label.personMakingComplaintIdentification')" label-for="personMakingComplaintIdentification" label-class="font-weight-bold">
                        <!-- Name -->
                        <label>{{ $t('label.Name') }}</label>
                        <div>
                          <validation-provider :name="`${$t('label.Name')} 1`" rules="required" v-slot="{ errors }">
                            <b-form-input
                              v-model="complaint.author.name"
                              trim
                              :placeholder="$t('placeholder.name')"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </div>

                        <!-- RUT -->
                        <label>{{ $t('message.tableHeader.rut') }}</label>
                        <div>
                          <validation-provider :name="`${$t('message.tableHeader.rut')} 1`" rules="required" v-slot="{ errors }">
                            <b-form-input
                              v-model="complaint.author.rut"
                              trim
                              :placeholder="$t('placeholder.rut')"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </div>

                        <!-- Email -->
                        <label>{{ $t('label.Email') }}</label>
                        <div>
                          <validation-provider :name="`${$t('label.Email')} 1`" rules="required|email" v-slot="{ errors }">
                            <b-form-input
                              v-model="complaint.author.email"
                              trim
                              :placeholder="$t('placeholder.email')"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </div>

                        <!-- Company -->
                        <label>{{ $t('label.company') }}</label>
                        <div>
                          <validation-provider :name="`${$t('label.company')} 1`" rules="required" v-slot="{ errors }">
                            <v-select
                              v-model="complaint.author.company"
                              label="title"
                              :options="companies"
                              :placeholder="$t('placeholder.company')"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </div>
                      </b-form-group>
                    </b-col>

                    <!-- Affected Person -->
                    <b-col md="4" class="border-right">
                      <b-form-group :label="$t('label.affectedPersonIdentification')" label-for="affectedPersonIdentification" label-class="font-weight-bold">
                        <!-- Author === Affected Person Checkbox -->
                        <b-form-checkbox v-model="isAuthorSameAsAffectedPerson" class="my-1">
                          <small>{{ $t('label.authorSameAsAffectedPerson') }}</small>
                        </b-form-checkbox>
                        
                        <!-- Name -->
                        <label>{{ $t('label.Name') }}</label>
                        <div>
                          <validation-provider :name="`${$t('label.Name')} 2`" rules="required" v-slot="{ errors }">
                            <b-form-input
                              v-model="complaint.affectedPerson.name"
                              trim
                              :disabled="isAuthorSameAsAffectedPerson"
                              :placeholder="$t('placeholder.name')"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </div>

                        <!-- RUT -->
                        <label>{{ $t('message.tableHeader.rut') }} {{ $t('message.(optional)') }}</label>
                        <b-form-input
                          v-model="complaint.affectedPerson.rut"
                          trim
                          :disabled="isAuthorSameAsAffectedPerson"
                          :placeholder="$t('placeholder.rut')"
                        />

                        <!-- Email -->
                        <label>{{ $t('label.Email') }}</label>
                        <div>
                          <validation-provider :name="`${$t('label.Email')} 2`" rules="required|email" v-slot="{ errors }">
                            <b-form-input
                              v-model="complaint.affectedPerson.email"
                              trim
                              :disabled="isAuthorSameAsAffectedPerson"
                              :placeholder="$t('placeholder.email')"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </div>

                        <!-- Company -->
                        <label>{{ $t('label.company') }}</label>
                        <div>
                          <validation-provider :name="`${$t('label.company')} 2`" rules="required" v-slot="{ errors }">
                            <v-select
                              v-model="complaint.affectedPerson.company"
                              label="title"
                              :disabled="isAuthorSameAsAffectedPerson"
                              :options="companies"
                              :placeholder="$t('placeholder.company')"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </div>
                      </b-form-group>
                    </b-col>

                    <!-- Reported Person -->
                    <b-col md="4">
                      <b-form-group :label="$t('label.reportedPersonIdentification')" label-for="reportedPersonIdentification" label-class="font-weight-bold">
                        <!-- Name -->
                        <label>{{ $t('label.Name') }}</label>
                        <div>
                          <validation-provider :name="`${$t('label.Name')} 3`" rules="required" v-slot="{ errors }">
                            <b-form-input
                              v-model="complaint.reportedPersonData.name"
                              trim
                              :placeholder="$t('placeholder.name')"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </div>

                        <!-- RUT -->
                        <!-- <label>{{ $t('message.tableHeader.rut') }} {{ $t('message.(optional)') }}</label>
                        <b-form-input
                          v-model="complaint.reportedPersonData.rut"
                          trim
                          :placeholder="$t('placeholder.rut')"
                        /> -->

                        <!-- Email -->
                        <label>{{ $t('label.Email') }} {{ $t('message.(optional)') }}</label>
                        <div>
                          <validation-provider :name="`${$t('label.Email')} 3`" rules="email" v-slot="{ errors }">
                            <b-form-input
                              v-model="complaint.reportedPersonData.email"
                              trim
                              :placeholder="$t('placeholder.email')"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </div>

                        <!-- Position -->
                        <label>{{ $t('label.position') }}</label>
                        <div>
                          <validation-provider :name="`${$t('label.position')} 3`" rules="required" v-slot="{ errors }">
                            <b-form-input
                              v-model="complaint.reportedPersonData.position"
                              trim
                              :placeholder="$t('placeholder.position')"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </div>

                        <!-- Company -->
                        <label>{{ $t('label.company') }}</label>
                        <div>
                          <validation-provider :name="`${$t('label.company')} 3`" rules="required" v-slot="{ errors }">
                            <v-select
                              v-model="complaint.reportedPersonData.company"
                              label="title"
                              :options="companies"
                              :placeholder="$t('placeholder.company')"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </div>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row class="mt-1">
                    <!-- Company Relationship -->
                    <b-col md="6">
                      <b-form-group :label="$t('label.organizationalLinkBetweenTheParties')" label-for="organizationalLinkBetweenTheParties">
                        <validation-provider :name="$t('label.link')" rules="required" v-slot="{ errors }">
                          <v-select
                            v-model="complaint.companyRelationship"
                            label="title"
                            :options="complaintRelationshipOptions"
                            :placeholder="$t('placeholder.link')"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <!-- Description -->
                    <b-col md="12">
                      <b-form-group :label="$t('label.factsThatAreReported')" label-for="factsThatAreReported">
                        <validation-provider :name="$t('label.factsThatAreReported')" rules="required" v-slot="{ errors }">
                          <b-form-textarea
                            v-model="complaint.description"
                            rows="2"
                            :placeholder="$t('message.describe_what_when_where')"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <!-- Document -->
                    <b-col md="6">
                      <b-form-group :label="$t('label.additionalBackgroundUpload')" label-for="additionalBackgroundUpload">
                        <!-- Document Repository -->
                        <document-repository
                          :documents="documentFiles"
                          :keys="[]"
                          :read-only="false"
                          @add="handleAddDocuments"
                          @delete="handleDeleteDocuments"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>
              </validation-observer>
            </tab-content>
          </form-wizard>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { BLink, BButton, BListGroup, BListGroupItem, BFormCheckbox, BFormTextarea, BFormInput } from 'bootstrap-vue'
import AddCoLogo from '@core/layouts/components/Logo.vue'
import { ref, onMounted, watch } from "@vue/composition-api"
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import useNotifications from "@/composables/useNotifications"
import { useRouter } from '@core/utils/utils'
import i18n from '@/libs/i18n'
import useJwt from '@/auth/jwt/useJwt'
import { FormWizard, TabContent } from "vue-form-wizard"
import "vue-form-wizard/dist/vue-form-wizard.min.css"
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { ValidationObserver, ValidationProvider, localize } from 'vee-validate'
import { required, is } from "@validations"
import vSelect from 'vue-select'
import { complaintThemes, complaintRelationshipOptions, karinLawThemes, inputFileTypes } from '@/constants'
import awsConnection from '@/views/habit/aws'
import useCommonTodo from '@/views/apps/todo/useCommonTodo'
import { useLambda } from '@/views/habit/awsLambda'
import DocumentRepository from '@/views/habit/DocumentRepository'
import { oid, date } from '@/helpers/mongoHelpers'

export default {
  components: {
    AddCoLogo,
    BLink,
    BButton,
    BListGroup,
    BListGroupItem,
    BFormCheckbox,
    BFormTextarea,
    BFormInput,
    Loading,
    FormWizard,
    TabContent,
    VuePerfectScrollbar,
    ValidationObserver,
    ValidationProvider,
    vSelect,
    DocumentRepository,
  },
  setup() {
    const { showSuccessMessage, showErrorMessage } = useNotifications();
    const { singleUpload } = awsConnection()
    const { route } = useRouter()
    const { sendEmailCase, uploadFileToS3 } = useCommonTodo()
    const { updateItem, getItem, getItems, createItem } = useLambda()
    const { clientPublicId } = route.value.params
    const clientData = ref({})
    const clientNotFound = ref(false)
    const employees = ref([])
    const areas = ref([])
    const companies = ref([])
    const isLoading = ref(true)
    const isSubmitting = ref(false)
    const isSubmitted = ref(false)
    const acceptTerms = ref('no')
    const typeTab = ref(null)
    const termsTab = ref(null)
    const complaintTab = ref(null)
    const complaint = ref({ author: {}, affectedPerson: {}, reportedPersonData: {} })
    const documentFiles = ref([])
    const type = ref("")
    const isAuthorSameAsAffectedPerson = ref(false)
    const myForm = ref(null)
    const tabRefs = [ typeTab, termsTab, complaintTab ]

    watch(type, () => {
      // Reset variables and form validations
      acceptTerms.value = 'no'
      complaint.value = { author: {}, affectedPerson: {}, reportedPersonData: {} }
      termsTab.value.reset()
      complaintTab.value.reset()
    })

    watch(isAuthorSameAsAffectedPerson, (val) => {
      if (val) {
        complaint.value.affectedPerson = { ...complaint.value.author }
      } else {
        complaint.value.affectedPerson = {
          name: '',
          rut: '',
          email: '',
          company: null
        }
      }
    })

    const getUniqueId = () => {
      return Math.floor(Date.now() * Math.random()).toString()
    }

    // Generate random unique numbers that will be associated with the complaint
    const authorUserNumber = getUniqueId()
    const affectedUserNumber = getUniqueId()
    const reportedUserNumber = getUniqueId()
    const caseNumber = getUniqueId()

    const handleAddDocuments = (newDocs) => {
      documentFiles.value.push(...newDocs)
    }

    const handleDeleteDocuments = (index) => {
      documentFiles.value.splice(index, 1)
    }

    const handleSubmit = async () => {
      try {
        await validateTab(complaintTab)
        formSubmitted()
      } catch (error) {
        // Si alguna validación falla, no enviamos
      }
    }

    const formSubmitted = async () => {
      isSubmitting.value = true

      try {
        if (complaint.value.reportedPerson?.length) {
          for (const reportedPerson of complaint.value.reportedPerson) {
            if (reportedPerson?.addNewOption) reportedPerson.value = await createNewWorker(reportedPerson.title)
          }
        }
        if (complaint.value.reportedPersonResponsible?.addNewOption) {
          complaint.value.reportedPersonResponsible.value = await createNewWorker(complaint.value.reportedPersonResponsible.title)
        }
        if (complaint.value.areas?.length) {
          for (const area of complaint.value.areas) {
            if (area?.addNewOption) area.value = await createNewArea(area.title)
          }
        }
      } catch (error) {
        showErrorMessage(i18n.t(`message.${error === 'workerError' ? 'worker_error' : 'location_add_error'}`))
        isSubmitting.value = false
        return
      }

      try {
        // Process company data for Karin Law
        const processedAuthor = complaint.value.author.name ? {
          ...complaint.value.author,
          company: complaint.value.author.company?.title || complaint.value.author.company
        } : null

        const processedAffectedPerson = complaint.value.affectedPerson.name ? {
          ...complaint.value.affectedPerson,
          company: complaint.value.affectedPerson.company?.title || complaint.value.affectedPerson.company
        } : null

        const processedReportedPersonData = complaint.value.reportedPersonData.name ? {
          ...complaint.value.reportedPersonData,
          company: complaint.value.reportedPersonData.company?.title || complaint.value.reportedPersonData.company
        } : null

        // Get the company ID (for Karin Law it's the reported person company ID)
        const companyId = type.value === 'karinLaw' && complaint.value.reportedPersonData.company?.value
          ? complaint.value.reportedPersonData.company.value
          : complaint.value.company?.value || null

        const payload = {
          client_id: oid(clientData.value._id),
          description: complaint.value.description,
          reportedPerson: complaint.value.reportedPerson ? complaint.value.reportedPerson.map(e => oid(e.value)) : null,
          reportedPersonResponsible: complaint.value.reportedPersonResponsible ? oid(complaint.value.reportedPersonResponsible.value) : null,
          areas: complaint.value.areas ? complaint.value.areas.map(e => oid(e.value)) : null,
          themes: complaint.value.themes?.map(e => ({ value: e.value === 'newOption' ? e.title : e.value, isNewOption: e.value === 'newOption' || null })),
          createdAt: date(new Date()),
          authorUserNumber,
          affectedUserNumber,
          reportedUserNumber,
          caseNumber,
          dueDate: date(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
          companyRelationship: !complaint.value.companyRelationship?.value ? null : {
            value: complaint.value.companyRelationship.value === 'newOption' ? complaint.value.companyRelationship.title : complaint.value.companyRelationship.value,
            isNewOption: complaint.value.companyRelationship.value === 'newOption' || null,
          },
          company: companyId ? oid(companyId) : null,
          stage: "registration",
          author: processedAuthor,
          affectedPerson: processedAffectedPerson,
          reportedPersonData: processedReportedPersonData,
          type: type.value,
          rootCause: 100,
          rootCauseCorrectiveMeasures: 100,
        }

        const { insertedId } = await createItem({ collection: 'complaint', payload })

        showSuccessMessage(i18n.t('message.complaint_success'))
          
        const complaintId = insertedId.toString()
        const destinationFolder = `${clientData.value._id}/complaints`
        
        const uploadDocumentsPromises = documentFiles.value?.map(file => {
          if (!file?.name) return null
          return new Promise((resolve, reject) => {
            uploadFileToS3(file, destinationFolder)
              .then((key) => resolve(key))
              .catch((err) => {
                console.log(err)
                resolve(null)
              })
          })
        }) || []

        const documentKeys = await Promise.all(uploadDocumentsPromises)

        updateComplaintWithKey(complaintId, documentKeys.filter(e => !!e))

        // Send email to the client complaint recipient with the details of the complaint created
        complaint.value._id = complaintId
        complaint.value.stage = payload.stage
        complaint.value.type = type.value
        complaint.value.companyId = companyId
        sendEmailCase(complaint.value, 'add', null, 'complaint', clientData.value.complaintRecipients)

        isSubmitted.value = true
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.complaint_submit_error'))
      } finally {
        isSubmitting.value = false
      }
    }

    const validateTab = (tabRef) => new Promise((resolve, reject) => {
      tabRef.value.validate().then(success => {
        if (success) resolve(true)
        else {
          showErrorMessage(i18n.t('message.requiredFieldsIncomplete'))
          reject()
        }
      })
    })

    const updateComplaintWithKey = async (complaintId, keys) => {
      try {
        const query = { _id: oid(complaintId) }
        const action = { $set: { documentKey: keys }, $push: { allFileKeys: { $each: keys } } }

        await updateItem({ collection: 'complaint', query, action })
      } catch (error) {
        console.log(error)
      }
    }

    const getEmployees = async (clientId) => {
      try {
        const conditions = {
          client_id: oid(clientId),
          deleted: { $ne: true }
        }
  
        const items = await getItems({ collection: 'worker', query: conditions, options: { sort: { name: 1 } } })

        employees.value = items?.map(({ name, _id, onlyDropdownOption }) => (
          { title: onlyDropdownOption ? i18n.t(`label.${name}`) : name, value: _id.toString() }
        )) || []
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.err_worker_list'));
      }
    }

    const getAreas = async (clientId) => {
      try {
        const conditions = {
          client_id: oid(clientId),
          deleted: { $ne: true }
        }

        const items = await getItems({ collection: 'location', query: conditions, options: { sort: { location: 1 } } })
        areas.value = items?.map(({ location, _id, onlyDropdownOption }) => (
          { title: onlyDropdownOption ? i18n.t(`label.${location}`) : location, value: _id.toString() }
        )) || []
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.location_list_fetch_error'));
      }
    }

    const getCompanies = async (clientId) => {
      try {
        const conditions = {
          client_id: oid(clientId),
          deleted: { $ne: true }
        }

        const items = await getItems({ collection: 'company', query: conditions, options: { sort: { name: 1 } } })
        companies.value = items?.map(({ name, _id }) => (
          { title: name, value: _id.toString() }
        )) || []
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.company_list_fetch_error'));
      }
    }

    const createNewWorker = (name) => {
      return new Promise(async (resolve, reject) => {
        try {
          const payload = {
            client_id: oid(clientData.value._id),
            name,
            privileges: 'employee'
          }

          const { insertedId } = await createItem({ collection: 'worker', payload })

          resolve(insertedId.toString())
        } catch (error) {
          console.log(error)
          reject("workerError")
        }
      })
    }

    const createNewArea = (name) => {
      return new Promise(async (resolve, reject) => {
        try {
          const payload = {
            client_id: oid(clientData.value._id),
            location: name,
            concept: 'VARIAS'
          }

          const { insertedId } = await createItem({ collection: 'location', payload })

          resolve(insertedId.toString())
        } catch (error) {
          console.log(error)
          reject("locationError")
        }
      })
    }

    const goToPreviousTab = () => {
      if (myForm.value) {
        myForm.value.prevTab()
      }
    }

    const goToNextTab = async () => {
      const index = myForm.value.activeTabIndex

      try {
        await validateTab(tabRefs[index])
        myForm.value.nextTab()
      } catch (err) {
        // Error de validación, no avanza
      }
    }

    onMounted(async () => {
      try {
        // If route doesn't have the client public id, throw error
        if (!clientPublicId) throw new Error("Client public id not present in route params")

        // If user is not logged in (either as a real or anonymous user), then log in as an anonymous user
        // if (!useJwt.realm.currentUser) await useJwt.loginAsAnonymousUser()

        // Get client data based on client public id from URL param
        const item = await getItem({ collection: 'client', query: { publicId: Number(clientPublicId) } })
        if (!item) throw new Error('Client not found')
        
        item._id = item._id.toString()
        clientData.value = item

        // Get data for dropdowns
        getEmployees(clientData.value._id)
        getAreas(clientData.value._id)
        getCompanies(clientData.value._id)

        // Define locale based on client default language
        i18n.locale = clientData.value.default_language
        localize(clientData.value.default_language)

      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.err_client_fetch'))
        clientNotFound.value = true

      } finally {
        isLoading.value = false
      }
    })

    return {
      clientData,
      clientNotFound,
      isLoading,
      isSubmitting,
      isSubmitted,
      acceptTerms,
      required,
      is,
      validateTab,
      typeTab,
      termsTab,
      complaintTab,
      complaint,
      employees,
      areas,
      companies,
      documentFiles,
      complaintThemes,
      complaintRelationshipOptions,
      authorUserNumber,
      caseNumber,
      type,
      karinLawThemes,
      isAuthorSameAsAffectedPerson,
      inputFileTypes,
      handleAddDocuments,
      handleDeleteDocuments,
      myForm,
      goToPreviousTab,
      goToNextTab,
      handleSubmit,
    };
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-misc.scss';
@import "@core/scss/vue/libs/vue-wizard.scss";
</style>

<style scoped>
.scroll-area {
  position: relative;
  max-height: 150px;
}
.border-right {
  border-right: 1px solid #ccc !important;
}
</style>